'use client'

import { useState, use, Suspense, useMemo, useCallback } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { SwapInput } from './SwapInput'
import { SwapButton } from './SwapButton'
import { TokenSelector } from './TokenSelector'
import { ArrowUpDown } from 'lucide-react'
import { useTokenLookup } from '@/hooks/useTokenLookup'
import type { Token } from '@/types/token'

// Fast URL token resolver using use() hook
const createURLTokenResolver = async (
  tokenInAddress: string | null,
  tokenOutAddress: string | null,
  findTokenByAddress: (address: string) => Promise<Token | null>
): Promise<{ sellToken: Token | null; buyToken: Token | null }> => {

  // Only fetch tokens that are specified in URL
  const promises = []

  if (tokenInAddress) {
    promises.push(findTokenByAddress(tokenInAddress))
  } else {
    promises.push(Promise.resolve(null))
  }

  if (tokenOutAddress) {
    promises.push(findTokenByAddress(tokenOutAddress))
  } else {
    promises.push(Promise.resolve(null))
  }

  const [sellToken, buyToken] = await Promise.all(promises)

  return { sellToken, buyToken }
}

interface SwapFormData {
  sellToken: Token
  buyToken: Token
  sellAmount: string
  buyAmount: string
}

function TokenAwareSwapForm() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const {
    findTokenByAddress,
    getDefaultTokens,
    allTokens,
    isConnected,
    walletAddress
  } = useTokenLookup()

  const [sellAmount, setSellAmount] = useState('1')
  const [buyAmount, setBuyAmount] = useState('0.000902444')
  const [isTokenSelectorOpen, setIsTokenSelectorOpen] = useState(false)
  const [tokenSelectorType, setTokenSelectorType] = useState<'sell' | 'buy'>('sell')

  // Get URL parameters
  const tokenInAddress = searchParams.get('tokenIn')
  const tokenOutAddress = searchParams.get('tokenOut')
  const hasURLTokens = tokenInAddress || tokenOutAddress

  // Get default tokens (synchronous)
  const { defaultSell, defaultBuy } = getDefaultTokens()

  // Create URL token resolution promise only when needed
  const urlTokenPromise = useMemo(() => {
    if (!hasURLTokens) return null

    return createURLTokenResolver(
      tokenInAddress,
      tokenOutAddress,
      findTokenByAddress
    )
  }, [tokenInAddress, tokenOutAddress, findTokenByAddress, hasURLTokens])

  // Resolve URL tokens with use() - suspends component until resolved
  const urlTokens = urlTokenPromise ? use(urlTokenPromise) : null

  // Final token selection with fallbacks
  const sellToken = urlTokens?.sellToken || defaultSell
  const buyToken = urlTokens?.buyToken || defaultBuy

  // Create swap form data for child components
  const swapData: SwapFormData = {
    sellToken,
    buyToken,
    sellAmount,
    buyAmount
  }

  // URL management
  const updateURL = useCallback((newSellToken: Token, newBuyToken: Token) => {
    const params = new URLSearchParams(searchParams.toString())
    params.set('tokenIn', newSellToken.address)
    params.set('tokenOut', newBuyToken.address)

    router.replace(`${window.location.pathname}?${params.toString()}`, {
      scroll: false
    })
  }, [router, searchParams])

  // Swap handler
  const handleSwapTokens = useCallback(() => {
    setSellAmount(buyAmount)
    setBuyAmount(sellAmount)
    updateURL(buyToken, sellToken)
  }, [buyAmount, sellAmount, buyToken, sellToken, updateURL])

  // Token selection handler
  const handleTokenSelect = useCallback((token: Token) => {
    if (tokenSelectorType === 'sell') {
      updateURL(token, buyToken)
    } else {
      updateURL(sellToken, token)
    }
    setIsTokenSelectorOpen(false)
  }, [tokenSelectorType, buyToken, sellToken, updateURL])

  const openTokenSelector = useCallback((type: 'sell' | 'buy') => {
    setTokenSelectorType(type)
    setIsTokenSelectorOpen(true)
  }, [])

  return (
    <>
      <div className="space-y-1">
        <SwapInput
          label="Sell"
          amount={sellAmount}
          onAmountChange={setSellAmount}
          token={sellToken}
          onTokenSelect={() => openTokenSelector('sell')}
          usdValue="0.34"
          balance={sellToken.balance || "0.00"}
        />

        <div className="flex justify-center -my-2 relative z-10">
          <button
            onClick={handleSwapTokens}
            className="bg-slate-700 hover:bg-slate-600 rounded-full p-2 border-4 border-slate-800 transition-colors"
          >
            <ArrowUpDown className="w-4 h-4 text-slate-300" />
          </button>
        </div>

        <SwapInput
          label="Buy"
          amount={buyAmount}
          onAmountChange={setBuyAmount}
          token={buyToken}
          onTokenSelect={() => openTokenSelector('buy')}
          usdValue="0.34"
          balance={buyToken.balance || "0.00"}
          readOnly={true}
        />

        <div className="pt-4">
          <SwapButton
            // swapData={swapData}
            // isConnected={isConnected}
            // walletAddress={walletAddress}
          />
        </div>
      </div>

      {isTokenSelectorOpen && (
        <TokenSelector
          tokens={allTokens}
          onTokenSelect={handleTokenSelect}
          onClose={() => setIsTokenSelectorOpen(false)}
          selectedToken={tokenSelectorType === 'sell' ? sellToken : buyToken}
          isConnected={isConnected}
        />
      )}
    </>
  )
}

// Optimized skeleton - shows actual structure immediately
function SwapFormSkeleton() {
  return (
    <div className="space-y-1">
      <SwapInput
        label="Sell"
        amount="1"
        onAmountChange={() => { }}
        token={null}
        onTokenSelect={() => { }}
        usdValue="0.34"
        balance="0.00"
      />

      <div className="flex justify-center -my-2 relative z-10">
        <div className="bg-slate-700 rounded-full p-2 border-4 border-slate-800">
          <ArrowUpDown className="w-4 h-4 text-slate-300" />
        </div>
      </div>

      <SwapInput
        label="Buy"
        amount="0.000902444"
        onAmountChange={() => { }}
        token={null}
        onTokenSelect={() => { }}
        usdValue="0.34"
        balance="0.00"
        readOnly={true}
      />

      <div className="pt-4">
        <SwapButton />
      </div>
    </div>
  )
}

export function SwapForm() {
  return (
    <Suspense fallback={<SwapFormSkeleton />}>
      <TokenAwareSwapForm />
    </Suspense>
  )
}